import "./Select.css";
import DropdownIcon from "../../../assets/icons/dropdown-icon.svg";
import { useEffect, useRef, useState } from "react";
const Select = ({
  className = "",
  label,
  icon,
  options = [],
  value,
  defaultValue = { label: "", value: "" },
  placeholder,
  onChange,
  error = false,
  disabled = false,
  isMulti = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(isMulti ? [] : defaultValue);

  const selectRef = useRef(null);

  useEffect(() => {
    if (isMulti) {
      if (Array.isArray(value)) {
        setSelectedOption(value);
      } else if (Array.isArray(defaultValue)) {
        setSelectedOption(defaultValue);
      }
    } else {
      const option =
        options.find((o) => o.value === value?.value) || options.find((o) => o.value === defaultValue?.value);

      setSelectedOption(option || null);
    }
  }, [value, defaultValue, options, isMulti]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // const handleOptionClick = (option) => {
  //   if (disabled) return;
  //   setSelectedOption(option);
  //   setIsOpen(false);
  //   if (onChange) {
  //     onChange(option);
  //   }
  // };

  const handleOptionClick = (option) => {
    // A single option can be disabled on its own, for choices that exist in the
    // design but are not available yet.
    if (disabled || option.disabled) return;

    if (isMulti) {
      const updated = [...selectedOption, option];
      setSelectedOption(updated);
      onChange?.(updated);
    } else {
      setSelectedOption(option);
      onChange?.(option);
      setIsOpen(false);
    }
  };

  const handleRemoveBadge = (option) => {
    const updated = selectedOption.filter((o) => o.value !== option.value);
    setSelectedOption(updated);
    onChange?.(updated);
  };

  const availableOptions = isMulti
    ? options.filter((opt) => !selectedOption.some((sel) => sel.value === opt.value))
    : options;

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  // Helper function to render icon
  const renderIcon = (iconElement) => {
    if (!iconElement) return null;

    // If icon is a string, treat it as image URL
    if (typeof iconElement === "string") {
      // Check if it's an SVG string
      if (iconElement.trim().startsWith("<svg")) {
        return <span className="custom-select-svg-icon" dangerouslySetInnerHTML={{ __html: iconElement }} />;
      }

      // Otherwise treat as image URL
      return <img src={iconElement} alt="" className="custom-select-img-icon" />;
    }

    // For React components or other elements
    return <span className="custom-select-icon">{iconElement}</span>;
  };

  return (
    <div
      className={`custom-select-container ${error ? "custom-select-error" : ""} ${
        disabled ? "custom-select-disabled" : ""
      } ${className}`}
      ref={selectRef}
      onClick={toggleDropdown}
      {...rest}
    >
      {/* Label section with icon */}
      {(icon || label) && (
        <div className="custom-select-label-section">
          {icon && renderIcon(icon)}
          {label && <div className="custom-select-label">{label}</div>}
        </div>
      )}

      {/* Selected option display */}
      {/* <div className="custom-select-value">
        {selectedOption ? (
          <>
            {selectedOption.icon && renderIcon(selectedOption.icon)}
            <span className="custom-select-text">{selectedOption.label}</span>
          </>
        ) : (
          <span className="custom-select-placeholder">{placeholder}</span>
        )}
      </div> */}
      <div className="custom-select-value">
        {isMulti ? (
          selectedOption.length > 0 ? (
            <div className="custom-select-badges">
              {selectedOption.map((option) => (
                <span key={option.value} className="custom-select-badge">
                  {option.label}
                  <span
                    className="custom-select-badge-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveBadge(option);
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          ) : (
            <span className="custom-select-placeholder">{placeholder}</span>
          )
        ) : selectedOption ? (
          <>
            {selectedOption.icon && renderIcon(selectedOption.icon)}
            <span className="custom-select-text">{selectedOption.label}</span>
          </>
        ) : (
          <span className="custom-select-placeholder">{placeholder}</span>
        )}
      </div>

      {/* Dropdown Arrow */}
      <div className="custom-select-arrow">
        <img src={DropdownIcon} alt="" />
      </div>

      {/* Options dropdown */}
      {isOpen && (
        <div className="custom-select-dropdown">
          {Array.isArray(availableOptions) && availableOptions.length > 0 ? (
            availableOptions.map((option) => (
              <div
                key={option.value}
                className={`custom-select-option ${
                  selectedOption && selectedOption.value === option.value ? "selected" : ""
                } ${option.disabled ? "custom-select-option-disabled" : ""}`}
                onClick={() => handleOptionClick(option)}
              >
                {option.icon && renderIcon(option.icon)}
                <span className="custom-select-option-label">{option.label}</span>
              </div>
            ))
          ) : (
            <div className="custom-select-no-options">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
